import classnames from "classnames"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM } from "@/pages/TrackingPage/DisplayOptions/components/themeLayoutAppWindow"
import { isShopifyEmbedded } from "@/utils/appBridge"
import toast from "@/utils/toast"

const TRACKING_PAGE_APPEARANCE_PATH = "/tracking-page/appearance"

export interface ThemeLayoutCustomizeAppWindowShellProps {
  heading: string
  children: React.ReactNode
  previewUrl?: string
  /** 是否显示保存/取消，由外部 SaveModule 状态驱动 */
  showSaveBar?: boolean
  onSave?: () => void
  onDiscard?: () => void
  saveLoading?: boolean
  saveDisabled?: boolean
}

/**
 * 使用 Polaris Web Components **`s-page`** + **`s-button`**（插槽与 Chrome demo 一致）。
 *
 * **宽度**：`s-page` 内部 Shadow DOM 会用 **`s-grid` + `min(100%, 966px)`** 包一层 **默认插槽**。
 * 任何 **未带 `slot` 的子节点**（例如正文 `div`）都会进默认槽 → 被限宽。**正文必须与 `s-page` 平级**，`s-page` 里只放 `slot="primary-action"` / `secondary-actions` 的 `s-button`。
 * 仍使用 **`inlineSize="large"`**（对应文档中的 `inline-size`，见 [Page](https://shopify.dev/docs/api/app-home/polaris-web-components/structure/page)）；
 * 宿主留白由 `app.scss` 中 `.pp-theme-layout-customize-s-page-toolbar` 收紧。
 *
 * 顶栏：嵌入 Shopify 且非超管时折叠本地 `s-page` 占位，标题与操作由 Admin / App Window 顶栏承接。
 * App Window / Modal iframe（`themeLayoutAppWindow=1`）内：Save / Discard 仅主文档 PPSaveBar；无脏数据时
 * 仍渲染 `primary-action` 的 Preview，供 App Window 标题栏拾取。
 */
const ThemeLayoutCustomizeAppWindowShell = ({
  heading,
  children,
  previewUrl,
  showSaveBar = false,
  onSave,
  onDiscard,
  saveLoading = false,
  saveDisabled = false,
}: ThemeLayoutCustomizeAppWindowShellProps) => {
  const { t } = useTranslation(["tracking_page", "common", "menu"])
  const navigate = useNavigate()
  const [showSlottedButtons, setShowSlottedButtons] = useState(false)
  const collapseShopifyToolbar = isShopifyEmbedded() && !window.isFromAdmin
  const isAppWindowContentFrame = useMemo(() => {
    if (typeof window === "undefined") {
      return false
    }

    return new URLSearchParams(window.location.search).get(THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM) === "1"
  }, [])

  const toolbarHeightClass = useMemo(() => {
    if (!collapseShopifyToolbar) {
      return "h-[60px]"
    }
    if (isAppWindowContentFrame && !showSaveBar) {
      return "h-0 min-h-0 overflow-visible border-0 border-b-0"
    }

    return "h-[0px] overflow-hidden"
  }, [collapseShopifyToolbar, isAppWindowContentFrame, showSaveBar])

  useEffect(() => {
    queueMicrotask(() => {
      setShowSlottedButtons(true)
    })
  }, [])

  const handlePreviewThisTheme = useCallback(() => {
    if (previewUrl) {
      window.open(previewUrl, "_blank", "noopener,noreferrer")
      return
    }
    toast(String(t("tracking_page:previewThisTheme")), 2e3)
  }, [previewUrl, t])

  const handleSaveClick = useCallback(() => {
    onSave?.()
  }, [onSave])

  const handleDiscardClick = useCallback(() => {
    onDiscard?.()
  }, [onDiscard])

  const previewLabel = String(t("tracking_page:previewThisTheme"))
  const saveLabel = String(t("common:Save"))
  const discardLabel = String(t("common:Discard"))

  const handleBackToTrackingPage = useCallback(() => {
    navigate(TRACKING_PAGE_APPEARANCE_PATH)
  }, [navigate])

  return (
    <div className="pp-theme-layout-customize-root flex min-h-0 w-full max-w-none min-w-0 flex-1 flex-col p-0">
      <div
        className={classnames(
          "pp-theme-layout-customize-s-page-toolbar shrink-0",
          isShopifyEmbedded() ? "isShopifyEmbedded" : "",
          toolbarHeightClass,
        )}
      >
        <s-page heading={heading} inlineSize="large">
          <s-link
            slot="breadcrumb-actions"
            href={TRACKING_PAGE_APPEARANCE_PATH}
            accessibilityLabel={String(t("common:Back"))}
            onClick={(event) => {
              event.preventDefault()
              handleBackToTrackingPage()
            }}
          >
            {t("menu:tracking_page")}
          </s-link>
          {showSlottedButtons && !(isAppWindowContentFrame && showSaveBar) ? (
            showSaveBar ? (
              <>
                <s-button
                  slot="primary-action"
                  variant="primary"
                  disabled={saveDisabled || saveLoading}
                  loading={saveLoading}
                  onClick={handleSaveClick}
                >
                  {saveLabel}
                </s-button>
                <s-button slot="secondary-actions" variant="secondary" onClick={handleDiscardClick}>
                  {discardLabel}
                </s-button>
              </>
            ) : (
              <s-button slot="primary-action" variant="primary" onClick={handlePreviewThisTheme}>
                {previewLabel}
              </s-button>
            )
          ) : null}
        </s-page>
      </div>

      <div className="box-border flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-0">
        {children}
      </div>
    </div>
  )
}

export default ThemeLayoutCustomizeAppWindowShell
